// Sectioned nav model. Pure derivation — given a `Principal`, returns
// the ordered list of nav sections (Quick Links, Rosters, Settings,
// Account) each containing the items visible to that principal.
// Sections with zero visible items are omitted entirely (header AND
// section both disappear) per `docs/navigation-redesign.md` §8.
//
// Items are a discriminated union:
//   - `kind: 'link'` — navigates to `to` on click. Renders as `<Link>`.
//   - `kind: 'external'` — opens an absolute URL outside the SPA router
//     (e.g. the static `/help/*` guides). Renders as a plain `<a href>`;
//     never carries active state. Used for the role-aware "Get Help"
//     Quick Link.
//   - `kind: 'action'` — runs a side-effect on click. Renders as
//     `<button>`. Used for Logout (Account section).
//
// The Ward Roster nav entry (§9) routes by role:
//   - Manager OR stake (with or without bishopric) → /stake/wards
//   - Bishopric only                                → /bishopric/roster

import type { LucideIcon } from 'lucide-react';
import {
  Bell,
  Building2,
  ClipboardList,
  Globe2,
  HelpCircle,
  Inbox,
  KeyRound,
  LayoutDashboard,
  LogOut,
  ScrollText,
  Settings,
  Table,
  Users,
} from 'lucide-react';
import type { Principal } from '../../lib/principal';
import { MANAGER_GUIDE_URL, REQUESTER_GUIDE_URL } from '../../lib/links';

interface NavItemBase {
  /** Stable key for React lists + active-state lookups. */
  key: string;
  label: string;
  icon: LucideIcon;
}

export interface NavLinkItem extends NavItemBase {
  kind: 'link';
  to: string;
}

export interface NavExternalItem extends NavItemBase {
  kind: 'external';
  /** Absolute URL outside the SPA router (e.g. a static `/help/*` guide). */
  href: string;
  /** Open in a new tab. Omitted/false → same tab. */
  newTab?: boolean;
}

export interface NavActionItem extends NavItemBase {
  kind: 'action';
  /** Discriminator for the runtime side-effect to run on click. */
  action: 'sign-out';
}

export type NavItem = NavLinkItem | NavExternalItem | NavActionItem;

export interface NavSection {
  /** Stable key. Section header text doubles as the screen-reader label. */
  key: 'quick-links' | 'rosters' | 'settings' | 'account' | 'superadmin';
  label: string;
  items: NavItem[];
}

function isManager(p: Principal, stakeId: string | null): boolean {
  if (p.isPlatformSuperadmin) return true;
  return stakeId !== null && p.managerStakes.includes(stakeId);
}

/**
 * Does the principal hold an actual manager CLAIM on this stake?
 *
 * Distinct from `isManager`, which treats a platform superadmin as one
 * everywhere — right for routing questions like `wardRosterPathFor`
 * ("where would this person's Ward Roster live"), wrong for deciding
 * what to put in the nav. A superadmin on a stake they hold no role on
 * was shown the entire manager section, and every page in it subscribes
 * `seats` / `requests` / `access`, which the rules deny for them. That
 * looks harmless — the lists render empty, the buttons sit disabled —
 * but each denied subscribe is a chance at the SDK's internal-assertion
 * panic, which reaches `RootErrorBoundary` as a full-page error rather
 * than anything the hooks can catch (see `useFirestoreDoc`'s header and
 * `useMemberDataStake`).
 *
 * Hiding those items costs that identity nothing it could use: the
 * pages have no data to show it. The one page that DOES work —
 * Configuration, for the Home Kindoo Site editor (§15) — is reached
 * from the Stake List row link, its documented entry point (T-91), and
 * the route still admits them (`holdsAnyRole` keeps its own superadmin
 * short-circuit). The Super Admin nav section is gated separately on
 * the literal claim and is unaffected.
 */
function hasManagerClaim(p: Principal, stakeId: string | null): boolean {
  return stakeId !== null && p.managerStakes.includes(stakeId);
}

function isStake(p: Principal, stakeId: string | null): boolean {
  return stakeId !== null && p.stakeMemberStakes.includes(stakeId);
}

function isBishopric(p: Principal, stakeId: string | null): boolean {
  if (stakeId === null) return false;
  const wards = p.bishopricWards[stakeId];
  return Array.isArray(wards) && wards.length > 0;
}

/**
 * Resolve the Ward Roster nav target. Manager or stake users — even if
 * they're also bishopric — go to the all-wards picker. Bishopric-only
 * users go to their per-ward roster.
 */
export function wardRosterPathFor(principal: Principal, stakeId: string | null): string {
  if (isManager(principal, stakeId) || isStake(principal, stakeId)) return '/stake/wards';
  return '/bishopric/roster';
}

/**
 * Build the nav sections visible to a principal. Empty sections (no
 * visible items) are omitted entirely.
 */
export function navSectionsForPrincipal(
  principal: Principal,
  stakeId: string | null,
): NavSection[] {
  const manager = hasManagerClaim(principal, stakeId);
  const stake = isStake(principal, stakeId);
  const bishopric = isBishopric(principal, stakeId);
  const anyRole = manager || stake || bishopric;
  // Logout keys off this, NOT `anyRole`. Signing out must never depend
  // on holding a role on the active stake: a platform superadmin
  // viewing a stake they hold none on would otherwise be left with no
  // way out of the app from the nav. That worked before only because
  // `isManager` short-circuited on the superadmin claim, which is
  // exactly what `hasManagerClaim` stops doing.
  const canSignOut = anyRole || principal.isPlatformSuperadmin;

  const quickLinks: NavItem[] = [];
  if (manager) {
    quickLinks.push({
      kind: 'link',
      key: 'dashboard',
      label: 'Dashboard',
      to: '/manager/dashboard',
      icon: LayoutDashboard,
    });
    quickLinks.push({
      kind: 'link',
      key: 'queue',
      label: 'Request Queue',
      to: '/manager/queue',
      icon: Inbox,
    });
  }
  if (anyRole) {
    quickLinks.push({
      kind: 'link',
      key: 'my-requests',
      label: 'My Requests',
      to: '/my-requests',
      icon: ClipboardList,
    });
    // Role-aware static help guide. Managers get the Kindoo Manager
    // guide; bishopric/stake get the requester guide. Last in Quick
    // Links. External (plain `<a href>`) — the guides are static files
    // outside the SPA router. Opens in a new tab so the user keeps their
    // place in the app while reading the guide.
    quickLinks.push({
      kind: 'external',
      key: 'get-help',
      label: 'Get Help',
      href: manager ? MANAGER_GUIDE_URL : REQUESTER_GUIDE_URL,
      newTab: true,
      icon: HelpCircle,
    });
  }

  const rosters: NavItem[] = [];
  if (manager || stake || bishopric) {
    rosters.push({
      kind: 'link',
      key: 'ward-roster',
      label: 'Ward Roster',
      to: wardRosterPathFor(principal, stakeId),
      icon: Users,
    });
  }
  if (manager || stake) {
    rosters.push({
      kind: 'link',
      key: 'stake-roster',
      label: 'Stake Roster',
      to: '/stake/roster',
      icon: Building2,
    });
  }
  if (manager) {
    rosters.push({
      kind: 'link',
      key: 'all-seats',
      label: 'All Seats',
      to: '/manager/seats',
      icon: Table,
    });
  }

  const settings: NavItem[] = [];
  if (manager) {
    // Operator-specified order: Configuration, App Access, Audit Log.
    // Audit Log stays at the bottom as a less-frequent path.
    settings.push({
      kind: 'link',
      key: 'configuration',
      label: 'Configuration',
      to: '/manager/configuration',
      icon: Settings,
    });
    settings.push({
      kind: 'link',
      key: 'access',
      label: 'App Access',
      to: '/manager/access',
      icon: KeyRound,
    });
    settings.push({
      kind: 'link',
      key: 'audit',
      label: 'Audit Log',
      to: '/manager/audit',
      icon: ScrollText,
    });
  }

  // Account section: visible to every authorized user. Notifications
  // is manager-only for-now (matching the route gate at
  // routes/_authed/notifications.tsx); future expansion to
  // bishopric/stake when push for completed/rejected/cancelled
  // ships.
  const account: NavItem[] = [];
  if (manager) {
    account.push({
      kind: 'link',
      key: 'notifications',
      label: 'Notifications',
      to: '/notifications',
      icon: Bell,
    });
  }
  if (canSignOut) {
    account.push({
      kind: 'action',
      key: 'logout',
      label: 'Logout',
      action: 'sign-out',
      icon: LogOut,
    });
  }

  // Superadmin section — gated strictly on the literal claim per
  // `navigation-redesign.md` §8. A Kindoo Manager who is NOT a
  // superadmin does not see this section even though they administer
  // the rest of the app.
  const superadmin: NavItem[] = [];
  if (principal.isPlatformSuperadmin) {
    superadmin.push({
      kind: 'link',
      key: 'superadmin-stakes',
      label: 'Stake List',
      to: '/superadmin/stakes',
      icon: Globe2,
    });
  }

  const out: NavSection[] = [];
  if (quickLinks.length > 0) {
    out.push({ key: 'quick-links', label: 'Quick Links', items: quickLinks });
  }
  if (rosters.length > 0) {
    out.push({ key: 'rosters', label: 'Rosters', items: rosters });
  }
  if (settings.length > 0) {
    out.push({ key: 'settings', label: 'Settings', items: settings });
  }
  if (superadmin.length > 0) {
    out.push({ key: 'superadmin', label: 'Super Admin', items: superadmin });
  }
  if (account.length > 0) {
    out.push({ key: 'account', label: 'Account', items: account });
  }
  return out;
}

/** Flatten a section list to its items in render order. */
export function flattenNavItems(sections: NavSection[]): NavItem[] {
  const out: NavItem[] = [];
  for (const s of sections) out.push(...s.items);
  return out;
}
